import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styles from '../../Products.module.scss'

import { ISelectOption } from 'components/elements/inputs/CustomSelect/CustomSelect'
import ProxyListConfiguration from './ProxyListConfiguration'
import ProxyListResult from './ProxyListResult'
import {
  Apps as DCIcon,
  MobilePhone as MobileIcon,
  Server as ISPIcon,
  Wifi as WifiIcon,
} from '../../../../icons'

import {
  selectProxyPackages,
  selectAvailableHosts,
  selectResellerStatus,
} from 'store/user/selectors'
import {
  randomCity,
  randomCountry,
  randomIsp,
  randomRegion,
} from 'utils/constants'
import { IProxyPackage } from 'models/user'
import { getDeepTargetResult, makeRandomString } from 'utils/functions'
import { ServiceActionsTypes } from 'store/service/actions'
import {
  getAvailableSessionIntervals,
  getProxyResultString,
} from 'utils/proxy/proxyFunctions'
import {
  dcSessionDefaultInterval,
  ispSessionDefaultInterval,
  mobileSessionDefaultInterval,
  residentialSessionDefaultInterval,
  proxyListResults,
  defaultOutputMaskOptionIndex,
  outputMaskOptions,
  defaultSpaceSymbol,
  defaultHostValue,
  resellerHostValue,
  defaultAmpersandSymbol,
  defaultProxyModifier,
  defaultBindTtlOption,
  bindTtlMinSeconds,
} from 'utils/proxy/proxyConstants'
import Flag from '../../../../elements/other/Flag'
// import { PricingActionsTypes } from 'store/pricing/actions'

const proxyTypesOptions = [
  {
    label: 'Residential proxies',
    shortLabel: 'Residential',
    value: 'wifi',
    icon: <WifiIcon />,
  },
  {
    label: 'Mobile proxies',
    shortLabel: 'Mobile',
    value: 'mobile',
    icon: <MobileIcon />,
  },
  {
    label: 'USA Datacenter proxies',
    shortLabel: 'DC',
    value: 'dc',
    icon: <DCIcon />,
  }
]

interface IProxyListGeneratorProps {
  activeProxySectionIndex: number
}

const ProxyListGenerator: React.FC<IProxyListGeneratorProps> = ({
  activeProxySectionIndex,
}) => {
  const dispatch = useDispatch()
  const proxyPackages = useSelector(selectProxyPackages)
  const availableHosts = useSelector(selectAvailableHosts)
  const isResellerMode = useSelector(selectResellerStatus)

  const [selectedProxyType, setProxyType] = useState<ISelectOption>(
    proxyTypesOptions[0]
  )
  const [selectedCountry, setCountry] = useState<ISelectOption>(randomCountry)
  const [selectedRegion, setRegion] = useState<ISelectOption>(randomRegion)
  const [selectedCity, setCity] = useState<ISelectOption>(randomCity)
  const [selectedIsp, setIsp] = useState<ISelectOption>(randomIsp)
  const [selectedTimeInterval, setTimeInterval] = useState<ISelectOption>({
    label: '300 secs',
    value: '300',
  })
  const [customTimeInterval, setCustomTimeInterval] = useState('')
  const [isBindIPActive, toggleBindIPActive] = useState(false)
  const [selectedBindTtl, setSelectedBindTtl] =
    useState<ISelectOption>(defaultBindTtlOption)
  const [customBindTtl, setCustomBindTtl] = useState('')
  const [activeSessionLengthType, setSessionLengthType] = useState<
    'same' | 'new'
  >('same')
  const [sessionPrefix, setSessionPrefix] = useState(makeRandomString(16))
  const [connectionsAmount, setConnectionsAmount] = useState(10)
  // const [listResult, setListResult] = useState<string[] | null>(null)
  // const [wasInitiallyGenerated, toggleInitiallyGenerated] = useState(false)
  const [selectedOutputMask, setOutputMask] = useState(
    outputMaskOptions[defaultOutputMaskOptionIndex]
  )
  const [spaceSymbol, setSpaceSymbol] = useState(defaultSpaceSymbol)
  const [ampersandSymbol, setAmpersandSymbol] = useState(defaultAmpersandSymbol)
  const [selectedProxyModifier, setProxyModifier] =
    useState(defaultProxyModifier)

  const selectedHostValue = isResellerMode
    ? resellerHostValue
    : defaultHostValue

  const getSelectedProxyPackage = () => {
    const selectedTypePackagesArray = proxyPackages.filter(
      (proxyPackage: IProxyPackage) => {
        return proxyPackage.connection_type === selectedProxyType.value
      }
    )

    return selectedTypePackagesArray[0]
  }

  const selectedProxyPackage = getSelectedProxyPackage()

  const availableSessionIntervals = getAvailableSessionIntervals(
    selectedProxyType.value
  )

  const deepTargetResult = getDeepTargetResult(
    spaceSymbol,
    ampersandSymbol,
    selectedRegion.value,
    selectedCity.value,
    selectedIsp.value
  )

  const timeInterval =
    selectedTimeInterval.value === 'custom'
      ? customTimeInterval
      : selectedTimeInterval.value

  const bindTtlValue = isBindIPActive
    ? selectedBindTtl.value === 'custom'
      ? customBindTtl || String(bindTtlMinSeconds)
      : selectedBindTtl.value
    : ''

  const getListResult = useCallback(() => {
    let listResult: string[] = []

    for (let i = 1; i <= connectionsAmount; i++) {
      listResult.push(
        getProxyResultString(
          // @ts-ignore
          proxyListResults[selectedOutputMask.value],
          selectedProxyPackage.id,
          selectedProxyPackage.package_key,
          selectedCountry.value,
          deepTargetResult,
          activeSessionLengthType,
          timeInterval,
          isBindIPActive,
          bindTtlValue,
          `${sessionPrefix}_x${i}`,
          selectedHostValue,
          selectedProxyModifier
        )
      )
    }

    return listResult
  }, [
    activeSessionLengthType,
    bindTtlValue,
    connectionsAmount,
    deepTargetResult,
    isBindIPActive,
    selectedCountry.value,
    selectedHostValue,
    selectedOutputMask.value,
    selectedProxyModifier,
    selectedProxyPackage.id,
    selectedProxyPackage.package_key,
    sessionPrefix,
    timeInterval,
  ])

  const listResult = getListResult()

  const handleProxyTypeChange = (option: any) => {
    setProxyType(option)

    if (option.value === 'isp' || option.value === 'dc') {
      handleCountryChange({
        label: 'United States',
        value: 'us',
        icon: <Flag countryCode={'us'} className={styles.optionFlag} />,
      })
    } else {
      handleCountryChange(randomCountry)

      dispatch({
        type: ServiceActionsTypes.SERVICE_GET_LOCATIONS,
        connectionType: option.value,
      })
    }

    handleCustomTimeIntervalChange('')

    handleRegionChange(randomRegion, false)
    handleCityChange(randomCity, false)
    handleIspChange(randomIsp)

    /*dispatch({
      type: PricingActionsTypes.PRICING_GET_PRODUCT_PRICE,
      productIdOrb: selectedProxyType.value,
    })*/
  }

  const handleCountryChange = (option: any) => {
    setCountry(option)
    setSessionPrefix(makeRandomString(16))

    handleRegionChange(randomRegion, false)
    handleCityChange(randomCity, false)
    handleIspChange(randomIsp)

    if (
      selectedProxyType.value !== 'isp' &&
      selectedProxyType.value !== 'dc' &&
      option.value !== 'any'
    ) {
      dispatch({
        type: ServiceActionsTypes.SERVICE_GET_LOCATION_TARGETS,
        data: { country: option.value, conn_type: selectedProxyType.value },
      })
    }
  }

  const handleRegionChange = (option: any, shouldRequest: boolean = true) => {
    setRegion(option)
    setSessionPrefix(makeRandomString(16))

    handleCityChange(randomCity, false)
    handleIspChange(randomIsp)

    if (shouldRequest) {
      dispatch({
        type: ServiceActionsTypes.SERVICE_GET_LOCATION_TARGETS,
        data: {
          conn_type: selectedProxyType.value,
          country: selectedCountry.value,
          region: option.value !== 'any' ? option.value : undefined,
        },
      })
    }
  }

  const handleCityChange = (option: any, shouldRequest: boolean = true) => {
    setCity(option)
    setSessionPrefix(makeRandomString(16))

    handleIspChange(randomIsp)

    if (shouldRequest) {
      dispatch({
        type: ServiceActionsTypes.SERVICE_GET_LOCATION_TARGETS,
        data: {
          conn_type: selectedProxyType.value,
          country: selectedCountry.value,
          region:
            selectedRegion.value !== 'any' ? selectedRegion.value : undefined,
          city: option.value !== 'any' ? option.value : undefined,
        },
      })
    }
  }

  const handleIspChange = (option: any) => {
    setIsp(option)
    setSessionPrefix(makeRandomString(16))
  }

  const handleDeepTargetsReset = () => {
    handleRegionChange(randomRegion, true)
  }

  useEffect(() => {
    handleCountryChange(randomCountry)
  }, [activeProxySectionIndex])

  const handleTimeIntervalChange = (option: any) => {
    setTimeInterval(option)
  }

  const handleCustomTimeIntervalChange = (value: string) => {
    setCustomTimeInterval(value)
  }

  const handleBindIP = () => {
    toggleBindIPActive(!isBindIPActive)
  }

  const handleCustomBindTtlChange = (val: string) => {
    setCustomBindTtl(val)
  }

  const handleBindTtlChange = (option: ISelectOption) => {
    setSelectedBindTtl(option)
  }

  const handleActiveSessionLengthChange = (value: 'new' | 'same') => {
    setSessionLengthType(value)
  }

  const handleSessionPrefixChange = (e: any) => {
    setSessionPrefix(e.target.value)
  }

  const handleConnectionsAmountChange = (val: any) => {
    setConnectionsAmount(val)
  }

  /*const handleGenerate = () => {
    const result = getListResult()

    setListResult(result)
  }*/

  const handleOutputMaskChange = (option: any) => {
    setOutputMask(option)
  }

  const handleSpaceSymbolChange = (value: string) => {
    setSpaceSymbol(value)
  }

  const handleAmpersandSymbolChange = (value: string) => {
    setAmpersandSymbol(value)
  }

  const handleProxyModifierChange = (value: string) => {
    setProxyModifier(value)
  }

  const handleGenerateIpClick = () => {
    setSessionPrefix(makeRandomString(16))
  }

  /*useEffect(() => {
    if (selectedCountry.value !== 'any' && !wasInitiallyGenerated) {
      handleGenerate()
      toggleInitiallyGenerated(true)
    }
  }, [wasInitiallyGenerated, selectedCountry.value, handleGenerate])*/

  useEffect(() => {
    if (selectedProxyType.value === 'wifi') {
      handleTimeIntervalChange(residentialSessionDefaultInterval)
    } else if (selectedProxyType.value === 'mobile') {
      handleTimeIntervalChange(mobileSessionDefaultInterval)
    } else if (selectedProxyType.value === 'dc') {
      handleTimeIntervalChange(dcSessionDefaultInterval)
    } else if (selectedProxyType.value === 'isp') {
      handleTimeIntervalChange(ispSessionDefaultInterval)
    }
  }, [selectedProxyType.value])

  return (
    <div className={styles.container}>
      <div
        className={`
          ${styles.contentBlock}
          ${styles.leftSide}
        `}
      >
        <ProxyListConfiguration
          selectedProxyType={selectedProxyType}
          selectedTimeInterval={selectedTimeInterval}
          customTimeInterval={customTimeInterval}
          selectedCountry={selectedCountry}
          selectedRegion={selectedRegion}
          selectedCity={selectedCity}
          selectedIsp={selectedIsp}
          isBindIPActive={isBindIPActive}
          selectedBindTtl={selectedBindTtl}
          customBindTtl={customBindTtl}
          activeSessionLengthType={activeSessionLengthType}
          sessionPrefix={sessionPrefix}
          proxyTypesOptions={proxyTypesOptions}
          selectedProxyModifier={selectedProxyModifier}
          availableSessionIntervals={availableSessionIntervals}
          // connectionsAmount={connectionsAmount}
          onProxyTypeChange={handleProxyTypeChange}
          onTimeIntervalChange={handleTimeIntervalChange}
          onCustomTimeIntervalChange={handleCustomTimeIntervalChange}
          onBindIp={handleBindIP}
          onBindTtlChange={handleBindTtlChange}
          onCustomBindTtlChange={handleCustomBindTtlChange}
          onActiveSessionLengthChange={handleActiveSessionLengthChange}
          onSessionPrefixChange={handleSessionPrefixChange}
          onCountryChange={handleCountryChange}
          onRegionChange={handleRegionChange}
          onCityChange={handleCityChange}
          onIspChange={handleIspChange}
          onDeepTargetsReset={handleDeepTargetsReset}
          onProxyModifierChange={handleProxyModifierChange}
          onGenerateIpClick={handleGenerateIpClick}
          // onConnectionsAmountChange={handleConnectionsAmountChange}
          // onGenerate={handleGenerate}
        />
      </div>

      <div
        className={`
          ${styles.contentBlock}
          ${styles.rightSide}
        `}
      >
        <ProxyListResult
          selectedProxyType={selectedProxyType}
          selectedOutputMask={selectedOutputMask}
          selectedSpaceSymbol={spaceSymbol}
          selectedAmpersandSymbol={ampersandSymbol}
          selectedHostValue={selectedHostValue}
          resultArray={listResult}
          connectionsAmount={connectionsAmount}
          onOutputMaskChange={handleOutputMaskChange}
          onSpaceSymbolChange={handleSpaceSymbolChange}
          onAmpersandSymbolChange={handleAmpersandSymbolChange}
          onConnectionsAmountChange={handleConnectionsAmountChange}
          onGenerateIpClick={handleGenerateIpClick}
        />
      </div>
    </div>
  )
}

export default ProxyListGenerator
